import { useQuery } from 'react-query';
import apiFetch from 'react-scale/lib/api-fetch';
import { ReviewLevel } from '@scale/shared-types';
import {
  GetProjectOnboardingFlowOutlineResponse,
  PublicCourseDetailsDTO,
  SyncProjectOnboardingStateResponse,
} from '@scale/shared/outlier/api';
import {
  CourseStepConfig,
  OnboardingFlowNodeType,
  OnboardingStepType,
  OutlierOnboardingResultType,
  TaskTrainingStepConfig,
} from '@scale/shared/onboardingControlPanel/index';
import {
  useProjectOutlineStore,
  useOutlineNodes,
} from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/stores/projectOutlineStore';
import { useRouter } from 'next/router';
import { useLogger } from 'react-scale/remotasks.com/components/Expert/utils';

type UseProjectOnboardingFlowInput = {
  onboardingFlowId?: string;
  projectId?: string;
  reviewLevel?: ReviewLevel;
};

export const useProjectOnboardingFlow = ({
  projectId = '',
  reviewLevel,
  onboardingFlowId = '',
}: UseProjectOnboardingFlowInput) => {
  const router = useRouter();
  const logger = useLogger();
  const setCourseMap = useProjectOutlineStore(state => state.setCourseMap);
  const setNodes = useProjectOutlineStore(state => state.setNodes);
  const setProjectName = useProjectOutlineStore(state => state.setProjectName);
  const isVisible = useProjectOutlineStore(state => state.isVisible);
  const setCurrentNodeIndex = useProjectOutlineStore(state => state.setCurrentNodeIndex);
  const setCompletedNodeIndex = useProjectOutlineStore(state => state.setCompletedNodeIndex);
  const outlineNodes = useOutlineNodes();
  const setPayRates = useProjectOutlineStore(state => state.setPayRates);

  const useOnboardingFlowId = !!onboardingFlowId;
  const useProjectIdAndReviewLevel = !!projectId && reviewLevel !== undefined;

  const { data: projectOnboardingFlow, isLoading: isLoadingProjectOnboardingFlow } = useQuery({
    queryKey: ['project-onboarding-flow', projectId, reviewLevel, onboardingFlowId],
    queryFn: () => {
      if (useOnboardingFlowId) {
        return apiFetch<GetProjectOnboardingFlowOutlineResponse>(
          null,
          `/internal/experts/project/onboarding-flow/${onboardingFlowId}`,
        );
      } else if (useProjectIdAndReviewLevel) {
        return apiFetch<GetProjectOnboardingFlowOutlineResponse>(
          null,
          `/internal/experts/project/onboarding-flow/${projectId}/${reviewLevel}`,
        );
      } else {
        return undefined;
      }
    },
    enabled: (useOnboardingFlowId || useProjectIdAndReviewLevel) && isVisible,
    onSuccess: projectOnboardingFlow => {
      if (projectOnboardingFlow) {
        const {
          trainingNodes,
          assessmentNodes,
          taskingNodes,
          externalName,
          taskingPayRate,
          assessmentPayRate,
          payRateType,
        } = projectOnboardingFlow;

        // Log flow data for debugging task-training visibility
        const taskTrainingNodes = trainingNodes?.filter(
          n => n.type === OnboardingStepType.TaskTraining,
        ) as TaskTrainingStepConfig[];
        logger.info('useProjectOnboardingFlow: received flow data', {
          logger_name: 'project-onboarding-flow',
          onboardingFlowId: projectOnboardingFlow.id,
          projectId: projectOnboardingFlow.projectId,
          reviewLevel: projectOnboardingFlow.reviewLevel,
          totalTrainingNodes: trainingNodes?.length ?? 0,
          taskTrainingNodeCount: taskTrainingNodes?.length ?? 0,
          taskTrainingNodeTitles: taskTrainingNodes?.map(n => n.practiceTaskTitle),
          allTrainingNodeTypes: trainingNodes?.map(n => n.type),
        });

        setProjectName(externalName);
        setNodes({
          trainingNodes,
          assessmentNodes,
          taskingNodes,
        });
        setPayRates({ taskingPayRate, assessmentPayRate, payRateType });
      }
    },
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingOnboardingState,
    data: projectOnboardingState,
    refetch: syncProjectOnboardingState,
    isRefetching: isSyncingProjectOnboardingState,
  } = useQuery({
    queryKey: [
      'project-onboarding-state',
      projectOnboardingFlow?.projectId,
      projectOnboardingFlow?.reviewLevel,
    ],
    queryFn: () => {
      return apiFetch<SyncProjectOnboardingStateResponse>(
        null,
        '/internal/experts/project/sync-onboarding-state',
        {
          method: 'POST',
          body: {
            projectId: projectOnboardingFlow?.projectId,
            reviewLevel: projectOnboardingFlow?.reviewLevel,
          },
        },
      );
    },
    onSuccess: data => {
      // If we've failed, return to the dashboard
      if (data.result === OutlierOnboardingResultType.Failed) {
        router.push('/expert');
        return;
      }

      // Find the nodes
      const updatedOutlineIndex = outlineNodes.findIndex(node => {
        if (
          data.result === OutlierOnboardingResultType.InProgress &&
          data.state.stepType === OnboardingStepType.Course &&
          node.type === OnboardingStepType.Course
        ) {
          return (data.state.config as CourseStepConfig)?.courseId === node.courseId;
        } else if (
          data.result === OutlierOnboardingResultType.InProgress &&
          data.state.stepType === OnboardingStepType.TaskTraining &&
          node.type === OnboardingStepType.TaskTraining
        ) {
          const config = data.state.config as unknown as TaskTrainingStepConfig;
          return config?.practiceTaskId === node.practiceTaskId;
        } else if (data.result === OutlierOnboardingResultType.InProgress) {
          return node.type === data.state.stepType;
        } else if (
          data.result === OutlierOnboardingResultType.Completed &&
          node.type === OnboardingFlowNodeType.Terminus
        ) {
          return node.success;
        }
      });
      setCompletedNodeIndex(updatedOutlineIndex);
      setCurrentNodeIndex(updatedOutlineIndex);
    },
    enabled: !!projectOnboardingFlow?.id,
    refetchOnWindowFocus: false,
  });

  const { isLoading: isLoadingCourses } = useQuery({
    queryKey: ['courses', projectOnboardingFlow?.id],
    queryFn: async () => {
      return Promise.all(
        projectOnboardingFlow!.trainingNodes
          .filter(node => 'courseId' in node)
          .map(node => {
            const nodeCourseId = (node as CourseStepConfig).courseId;
            const endpoint = `/internal/courses/${nodeCourseId}/public-details`;
            return apiFetch<{ course: PublicCourseDetailsDTO }>(null, endpoint);
          }),
      );
    },
    onSuccess: courses => {
      const courseMap = (courses ?? []).reduce<Record<string, PublicCourseDetailsDTO>>(
        (acc, { course }) => {
          acc[course._id] = course;
          return acc;
        },
        {},
      );

      setCourseMap(courseMap);
    },
    enabled: !!projectOnboardingFlow?.id,
    refetchOnWindowFocus: false,
  });

  return {
    isLoadingProjectOnboardingFlow,
    isLoadingCourses,
    isLoadingOnboardingState,
    isSyncingProjectOnboardingState,
    projectOnboardingFlow,
    projectOnboardingState,
    syncProjectOnboardingState,
  };
};
